import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { PlainDate } from '@paybae/shared'

import {
  type Account,
  type CardInput,
  type Category,
  type PaymentSource,
  type RecurringExpense,
  type RecurringPatch,
  type ReviewFrequency,
  type SavingsGoal,
  type IncomeSourceInput,
  type NotificationType,
  type RecordatorioInput,
  type Treatment,
  type InstallmentPlanInput,
  accounts,
  cards,
  categories,
  dashboard,
  household,
  incomes,
  installments,
  notifications,
  reconciliation,
  recurring,
  reports,
  reviews,
  savings,
  transactions,
  unwrap,
} from '@/lib/api'
import { borrarAdjunto, subirAdjunto, type DestinoAdjunto } from '@/lib/adjuntos'

/**
 * Consultas y mutaciones.
 *
 * Las claves se centralizan aquí para que invalidar tras una escritura sea una
 * decisión explícita y no una cadena repetida por las pantallas: un gasto nuevo
 * cambia el tablero, la lista, el saldo de la cuenta y —si fue a tarjeta— la
 * tarjeta. Olvidar uno de esos deja la app mostrando cifras viejas, que es la
 * peor forma de fallar en una app de dinero.
 */

export const keys = {
  session: ['session'] as const,
  household: ['household'] as const,
  invitations: ['household', 'invitations'] as const,
  accounts: ['accounts'] as const,
  categories: ['categories'] as const,
  cards: ['cards'] as const,
  /**
   * Compras a meses. El calendario de un plan cuelga del mismo prefijo para que
   * invalidar `['installments']` tras liquidar o cancelar alcance también a la
   * hoja de detalle que en ese momento está abierta encima de la lista.
   */
  meses: (filtros?: unknown) => ['installments', 'lista', filtros ?? {}] as const,
  calendarioMeses: (id: string) => ['installments', 'calendario', id] as const,
  transactions: (filtros?: unknown) => ['transactions', filtros ?? {}] as const,
  recurring: ['recurring'] as const,
  goals: ['savings', 'goals'] as const,
  /** Una nómina suelta. Cuelga de `income-events` para que la invalidación en bloque la alcance. */
  incomeEvent: (id: string) => ['income-events', 'uno', id] as const,
  /**
   * Reparto propuesto de una nómina. Cuelga de `savings` porque cambia con los
   * fondos y sus porcentajes, no solo con la nómina.
   */
  repartoPropuesto: (incomeEventId: string) =>
    ['savings', 'allocations', 'preview', incomeEventId] as const,
  /**
   * Reparto guardado de un integrante. Lleva el `memberId` en la clave porque el
   * de cada quien es distinto: sin él, abrir el de la pareja mostraría el propio
   * desde la caché.
   */
  reparto: (memberId?: string) => ['savings', 'allocations', 'plan', memberId ?? null] as const,
  reconciliation: ['reconciliation'] as const,
  /**
   * Bandeja, reglas y recordatorios cuelgan todos de `notifications` para que
   * marcar la bandeja como vista pueda invalidar el grupo entero de un golpe.
   */
  avisos: ['notifications', 'inbox'] as const,
  reglasAviso: ['notifications', 'rules'] as const,
  recordatorios: ['notifications', 'reminders'] as const,
  /** Revisiones conjuntas: la próxima, el historial y la cadencia, todo junto (§11). */
  reviews: ['reviews'] as const,
  planVsReal: (months: number) => ['reports', 'plan-vs-real', months] as const,
  dashboard: (scope?: string, memberId?: string) => ['dashboard', scope ?? 'combined', memberId ?? null] as const,
}

/** Todo lo que un movimiento nuevo puede haber cambiado. */
const TRAS_MOVIMIENTO = [
  ['dashboard'],
  ['transactions'],
  ['accounts'],
  ['cards'],
  ['savings'],
  // Cada movimiento cambia cuánto del ingreso está explicado: sin esto, un
  // gasto recién capturado seguiría contando como dinero sin justificar (§12).
  ['reconciliation'],
] as const

export function useDashboard(scope?: 'combined' | 'joint' | 'personal', memberId?: string) {
  return useQuery({
    queryKey: keys.dashboard(scope, memberId),
    queryFn: () => dashboard.get(scope, memberId),
  })
}

/** Gasto del mes y su reparto por categoría: lo que de verdad se busca al abrir. */
export function useCashflow(months = 6) {
  return useQuery({
    queryKey: ['dashboard', 'cashflow', months],
    queryFn: () => dashboard.cashflow(months),
  })
}

export function useHousehold() {
  return useQuery({ queryKey: keys.household, queryFn: () => household.get() })
}

export function useAccounts() {
  return useQuery({
    queryKey: keys.accounts,
    queryFn: async () => unwrap<Account>(await accounts.list(), 'accounts'),
  })
}

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: async () => unwrap<Category>(await categories.list(), 'categories'),
    // Las categorías cambian de año en año, no de minuto en minuto.
    staleTime: 10 * 60 * 1000,
  })
}

export function useCards() {
  return useQuery({ queryKey: keys.cards, queryFn: () => cards.list() })
}

/**
 * Movimientos ya filtrados por el servidor.
 *
 * Devuelve la página completa —no solo `items`— porque `total` es la única
 * fuente honesta de cuántos movimientos hay: al filtrar por integrante o por
 * mes, contar lo que llegó en la página diría otra cosa.
 */
export function useTransactions(filtros: Parameters<typeof transactions.list>[0] = {}) {
  return useQuery({
    queryKey: keys.transactions(filtros),
    queryFn: () => transactions.list(filtros),
  })
}

/**
 * Un movimiento concreto.
 *
 * `enabled` colgado del id: la hoja de detalle se monta siempre y solo recibe
 * un id cuando se abre. Sin esa condición pediría el movimiento `null` en cada
 * render de la lista.
 */
export function useTransaction(id: string | null) {
  return useQuery({
    queryKey: ['transactions', 'detalle', id],
    queryFn: () => transactions.get(id as string),
    enabled: id !== null,
  })
}

export function useGoals() {
  return useQuery({
    queryKey: keys.goals,
    queryFn: async () => unwrap<SavingsGoal>(await savings.goals(), 'goals'),
  })
}

export function useInvitations() {
  return useQuery({ queryKey: keys.invitations, queryFn: () => household.invitations.list() })
}

// ── Gastos fijos ────────────────────────────────────────────────────────────

export function useRecurring() {
  return useQuery({
    queryKey: keys.recurring,
    queryFn: async () => unwrap<RecurringExpense>(await recurring.list(), 'recurring'),
  })
}

/**
 * Alta y edición de un gasto fijo tocan el tablero además de la lista: lo
 * comprometido del periodo sale de aquí, y dejarlo sin refrescar mostraría un
 * disponible más alto del real justo después de registrar una renta.
 */
const TRAS_GASTO_FIJO = [['recurring'], ['dashboard']] as const

/**
 * Registrar la ocurrencia crea un movimiento de verdad: baja el saldo de la
 * cuenta, o engorda el estado de cuenta de la tarjeta. Invalida lo mismo que
 * una captura manual, más la propia lista de fijos —cuyo próximo vencimiento
 * acaba de moverse.
 */
const TRAS_OCURRENCIA = [['recurring'], ...TRAS_MOVIMIENTO] as const

export function useCrearGastoFijo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: recurring.create,
    onSuccess: () => invalidar(qc, TRAS_GASTO_FIJO),
  })
}

export function useActualizarGastoFijo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & RecurringPatch) => recurring.update(id, input),
    onSuccess: () => invalidar(qc, TRAS_GASTO_FIJO),
  })
}

export function useRegistrarOcurrencia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: { id: string; amountCents?: number; occurredOn?: PlainDate; notes?: string }) =>
      recurring.post(id, input),
    onSuccess: () => invalidar(qc, TRAS_OCURRENCIA),
  })
}

// ── Justificación del dinero (§12) ──────────────────────────────────────────

/** Cuánto del ingreso del periodo está explicado y qué falta por clasificar. */
export function useJustificacion() {
  return useQuery({
    queryKey: keys.reconciliation,
    queryFn: () => reconciliation.summary(),
  })
}

/**
 * Lo que mueve marcar un fijo desde la justificación, **menos** la propia
 * justificación: `mark-paid` responde con el resumen ya recalculado y ese se
 * siembra en la caché. Invalidarlo además pediría por red algo que ya está en
 * la mano, y el anillo parpadearía a mitad de su animación.
 */
const TRAS_MARCAR_PAGADO = [
  ['recurring'],
  ['dashboard'],
  ['transactions'],
  ['accounts'],
  ['cards'],
] as const

/**
 * Marca un gasto fijo como pagado desde la pantalla de justificación.
 *
 * No es lo mismo que `useRegistrarOcurrencia`: aquel postea cualquier
 * ocurrencia y este solo acepta las que vencen en el periodo en curso, que es
 * justo la garantía que evita que un segundo toque cargue la renta del mes que
 * viene. Por eso son dos rutas distintas y no una con parámetros.
 */
export function useMarcarFijoPagado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: reconciliation.markPaid,
    onSuccess: (resumen) => {
      qc.setQueryData(keys.reconciliation, resumen)
      invalidar(qc, TRAS_MARCAR_PAGADO)
    },
  })
}

/**
 * Plan contra realidad (§27).
 *
 * Los meses cerrados ya no cambian y el mes en curso se mueve con cada gasto,
 * pero el informe se pide entero: partirlo en dos consultas para ahorrar el
 * refresco de los cinco meses viejos costaría más código del que ahorra red.
 */
export function useInformes(months = 6) {
  return useQuery({
    queryKey: keys.planVsReal(months),
    queryFn: () => reports.planVsReal(months),
  })
}

export interface NuevoGasto {
  amountCents: number
  occurredOn: PlainDate
  description: string
  categoryId?: string
  attributedToMemberId: string | null
  paymentSource: PaymentSource
  accountId?: string
  creditCardId?: string
}

export function useCrearGasto() {
  const qc = useQueryClient()
  return useMutation({
    // `type` y `splitMode` no tienen valor por defecto en el servidor: se piden
    // explícitos para que nadie registre un traspaso creyendo que es un gasto,
    // ni reparta entre integrantes sin decidirlo. El capturador siempre crea un
    // gasto personal; el reparto se ajusta después desde el detalle.
    mutationFn: (input: NuevoGasto) =>
      transactions.create({ type: 'EXPENSE', splitMode: 'PERSONAL', ...input }),
    onSuccess: () => invalidar(qc, TRAS_MOVIMIENTO),
  })
}

export function useBorrarGasto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => transactions.remove(id),
    onSuccess: () => invalidar(qc, TRAS_MOVIMIENTO),
  })
}

export function useCrearCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: accounts.create,
    onSuccess: () => invalidar(qc, [['accounts'], ['dashboard']]),
  })
}

export function useCrearTarjeta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cards.create,
    onSuccess: () => invalidar(qc, [['cards'], ['dashboard']]),
  })
}

export function useActualizarTarjeta() {
  const qc = useQueryClient()
  return useMutation({
    // Cambiar el día de corte o el plazo mueve todas las fechas derivadas y el
    // servidor rehace los ciclos abiertos; el tablero anuncia esos vencimientos,
    // así que también se invalida.
    mutationFn: ({ id, ...input }: { id: string } & Partial<CardInput>) =>
      cards.update(id, input),
    onSuccess: () => invalidar(qc, [['cards'], ['dashboard']]),
  })
}

export function useArchivarTarjeta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => cards.archive(id),
    onSuccess: () => invalidar(qc, [['cards'], ['dashboard']]),
  })
}

/**
 * Pago de un estado de cuenta.
 *
 * Mueve dinero de verdad: abona a la tarjeta, crea el movimiento `CARD_PAYMENT`
 * y baja el saldo de la cuenta de origen. Los tres quedan obsoletos a la vez, y
 * dejar cualquiera sin invalidar mostraría un saldo que ya no existe.
 */
export function usePagarTarjeta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      cardId,
      statementId,
      ...input
    }: {
      cardId: string
      statementId: string
      amountCents: number
      fromAccountId: string
    }) => cards.pay(cardId, statementId, input),
    onSuccess: () => invalidar(qc, TRAS_MOVIMIENTO),
  })
}

// ── Compras a meses (MSI/MCI) ───────────────────────────────────────────────

/**
 * Todo lo que mueve registrar, editar, liquidar o cancelar una compra a meses.
 *
 * Las tres claves son obligatorias y ninguna es de adorno:
 * - `installments`, la lista y el calendario del plan;
 * - `cards`, porque la mensualidad entra en el estado de cuenta del corte y la
 *   tarjeta la enseña separada del consumo;
 * - `dashboard`, porque una mensualidad es dinero comprometido y el disponible
 *   la descuenta desde el momento en que el plan existe.
 */
const TRAS_COMPRA_MESES = [['installments'], ['cards'], ['dashboard']] as const

/**
 * Las compras a meses del hogar.
 *
 * Se piden **siempre con las canceladas incluidas** y el filtrado por estado se
 * hace en la pantalla: así el conmutador de «ver liquidadas» no dispara una
 * consulta nueva ni deja la lista en blanco medio segundo, y los totales del
 * encabezado no cambian de base al encenderlo.
 */
export function useMeses(filtros: Parameters<typeof installments.list>[0] = {}) {
  return useQuery({
    queryKey: keys.meses(filtros),
    queryFn: () => installments.list({ includeCancelled: true, ...filtros }),
  })
}

/** El calendario completo de un plan: una fila por mensualidad. */
export function useCalendarioMeses(id: string | null) {
  return useQuery({
    queryKey: keys.calendarioMeses(id ?? ''),
    queryFn: () => installments.schedule(id as string),
    enabled: id !== null && id !== '',
  })
}

export function useCrearCompraMeses() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InstallmentPlanInput) => installments.create(input),
    onSuccess: () => invalidar(qc, TRAS_COMPRA_MESES),
  })
}

export function useActualizarCompraMeses() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof installments.update>[1]) =>
      installments.update(id, input),
    onSuccess: () => invalidar(qc, TRAS_COMPRA_MESES),
  })
}

/**
 * Liquidar por adelantado.
 *
 * No hace desaparecer la deuda: lo que faltaba deja de cobrarse mes a mes y se
 * cobra de golpe en el estado de cuenta abierto, que es donde el banco lo pone.
 * Por eso invalida la tarjeta igual que el resto.
 */
export function useLiquidarCompraMeses() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; amountCents?: number; settledOn?: PlainDate }) =>
      installments.settle(id, input),
    onSuccess: () => invalidar(qc, TRAS_COMPRA_MESES),
  })
}

export function useCancelarCompraMeses() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => installments.cancel(id),
    onSuccess: () => invalidar(qc, TRAS_COMPRA_MESES),
  })
}

export function useCrearMeta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: savings.createGoal,
    onSuccess: () => invalidar(qc, [['savings'], ['dashboard']]),
  })
}

export function useActualizarMeta(goalId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof savings.updateGoal>[1]) =>
      savings.updateGoal(goalId, input),
    onSuccess: () => invalidar(qc, [['savings'], ['dashboard']]),
  })
}

/** Reparto guardado: qué parte del ingreso va a cada fondo (§5). */
export function useReparto(memberId?: string) {
  return useQuery({
    queryKey: keys.reparto(memberId),
    queryFn: () => savings.allocations(memberId),
  })
}

/**
 * Guardar el reparto.
 *
 * Se manda el plan **completo**, no lo que cambió: el `PUT` sustituye el reparto
 * entero y los fondos que no viajen se quedan en cero. Invalida `savings` —del
 * que cuelga el propio plan y el reparto propuesto de cada nómina— y el tablero,
 * porque el ahorro comprometido del periodo sale de aquí.
 */
export function useGuardarReparto(memberId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (allocations: ReadonlyArray<{ goalId: string; percent: number }>) =>
      savings.replaceAllocations({ memberId, allocations }),
    onSuccess: () => invalidar(qc, [['savings'], ['dashboard']]),
  })
}

/**
 * Aportar a una meta.
 *
 * Invalida lo mismo que un movimiento: una aportación **traspasada** mueve el
 * saldo de dos cuentas y deja un movimiento registrado, así que dejar fuera
 * `accounts` o `transactions` mostraría un saldo viejo justo después de mover
 * el dinero. Una aportación comprometida no mueve saldos, pero sí el
 * disponible del tablero, y desde el cliente no se sabe cuál de las dos fue
 * hasta que responde el servidor.
 */
export function useAportarMeta(goalId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof savings.contribute>[1]) =>
      savings.contribute(goalId, input),
    onSuccess: () => invalidar(qc, TRAS_MOVIMIENTO),
  })
}

export function useActualizarIntegrante() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, ...input }: { memberId: string } & Record<string, unknown>) =>
      household.updateMember(memberId, input),
    onSuccess: () => invalidar(qc, [['household'], ['dashboard'], ['session']]),
  })
}

/** El propio perfil: apodo, trato y color. No hace falta ser administrador. */
export function useActualizarMiPerfil() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      nickname?: string | null
      treatment?: Treatment
      personColorToken?: string
    }) => household.updateSelf(input),
    onSuccess: () => invalidar(qc, [['household'], ['dashboard'], ['session']]),
  })
}

/**
 * Perfil de nómina de un integrante.
 *
 * Comparte clave con `OnboardingGate`, que usa la existencia del perfil para
 * decidir si el asistente ya se completó. Guardar desde Ajustes invalida esa
 * misma entrada, así que ambos hablan siempre del mismo dato.
 */
/** Todas las fuentes de ingreso de un integrante. Puede tener varias. */
export function useIngresos(memberId: string) {
  return useQuery({
    queryKey: ['incomes', memberId],
    queryFn: () => incomes.list(memberId),
  })
}

/** Nóminas esperadas que faltan por confirmar. Alimenta el aviso de «¿ya te llegó?». */
export function useIngresosPorConfirmar(upTo?: PlainDate) {
  return useQuery({
    queryKey: ['income-events', 'SCHEDULED', upTo ?? null],
    queryFn: () => incomes.events.list({ status: 'SCHEDULED', upTo }),
  })
}

/**
 * Una nómina suelta, por id.
 *
 * La pantalla de confirmación entra por enlace directo —desde el aviso del
 * inicio o desde una notificación—, así que no puede depender de que la lista
 * del día siga en caché ni de que esa nómina siga estando `SCHEDULED`: al
 * confirmarla sale de la lista, y sacarla de ahí dejaría la pantalla en blanco
 * justo después de guardar.
 */
export function useIngreso(id: string) {
  return useQuery({
    queryKey: keys.incomeEvent(id),
    queryFn: () => incomes.events.get(id),
    enabled: id !== '',
  })
}

/**
 * Reparto propuesto de una nómina entre los fondos de ahorro.
 *
 * Solo propone: el servidor calcula y no escribe nada, así que se puede pedir
 * las veces que haga falta. Se consulta bajo demanda —`habilitado`— porque solo
 * hace falta en el último paso de la confirmación, y pedirlo antes gastaría una
 * ida al servidor en una pantalla que quizá se cierre en el primero.
 */
export function useRepartoPropuesto(incomeEventId: string, habilitado = true) {
  return useQuery({
    queryKey: keys.repartoPropuesto(incomeEventId),
    queryFn: () => savings.allocationPreview(incomeEventId),
    enabled: habilitado && incomeEventId !== '',
  })
}

/**
 * Alta, edición y baja de una fuente de ingreso.
 *
 * Una fuente define el periodo de gasto y el ingreso esperado, así que tocarla
 * recalcula el disponible entero — no solo la ficha de quien la editó.
 */
export function useGuardarIngreso(memberId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<IncomeSourceInput> & { id?: string }) =>
      id
        ? incomes.update(memberId, id, input)
        : incomes.create(memberId, input as IncomeSourceInput),
    onSuccess: () => invalidar(qc, [['incomes'], ['income-events'], ['dashboard']]),
  })
}

export function useBorrarIngreso(memberId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => incomes.remove(memberId, id),
    onSuccess: () => invalidar(qc, [['incomes'], ['income-events'], ['dashboard']]),
  })
}

/**
 * Registrar dinero que ya entró.
 *
 * Toca casi todo: sube el saldo de una cuenta, crea un movimiento, cambia el
 * disponible y —si va contra una fuente sin calendario— su promedio. Dejar
 * cualquiera de esos sin refrescar enseñaría cifras viejas justo después de
 * registrar dinero, que es cuando más se mira.
 */
export function useRegistrarIngreso() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof incomes.received>[0]) => incomes.received(input),
    onSuccess: () =>
      invalidar(qc, [
        ['dashboard'],
        ['accounts'],
        ['transactions'],
        ['incomes'],
        ['income-events'],
        ['reconciliation'],
      ]),
  })
}

/**
 * Confirma cuánto llegó de verdad de una nómina esperada (§4).
 *
 * Con cuenta destino mueve dinero real: sube el saldo y deja un movimiento de
 * tipo `INCOME`. Además cambia la base del reparto propuesto —pasa de repartir
 * lo estimado a repartir lo que entró—, y por eso se invalida `savings`: sin
 * ello el paso del ahorro seguiría proponiendo sobre una cifra que ya se sabe
 * equivocada.
 */
export function useConfirmarIngreso() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: Parameters<typeof incomes.events.confirm>[1] & { id: string }) =>
      incomes.events.confirm(id, input),
    onSuccess: () =>
      invalidar(qc, [
        ['income-events'],
        ['dashboard'],
        ['accounts'],
        ['transactions'],
        ['savings'],
      ]),
  })
}

/**
 * Registra cuánto se ahorró de verdad de ese ingreso (§6).
 *
 * Se invalida `savings` entero, no `goals`: la clave de las metas es
 * `['savings', 'goals']`, así que invalidar `['goals']` no alcanzaría ninguna
 * consulta —React Query compara por prefijo— y la pantalla de Ahorro seguiría
 * mostrando la cifra anterior sin que nada avisara del fallo.
 */
export function useAhorroDelIngreso() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, savedCents }: { id: string; savedCents: number }) =>
      incomes.events.savings(id, savedCents),
    onSuccess: () => invalidar(qc, [['income-events'], ['dashboard'], ['savings']]),
  })
}

export function useActualizarHogar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: household.update,
    // `countExpectedIncome` decide si el tablero anticipa lo que falta por caer,
    // y el nombre del hogar viaja dentro de la sesión.
    //
    // `periodAnchorDay` mueve la ventana sobre la que se mide *todo*: hay que
    // tirar también los informes y la conciliación, que traen su periodo ya
    // calculado del servidor. Y la sesión no es opcional aquí —es de donde
    // Movimientos y Pagos del periodo leen el ancla para calcularlo ellos—.
    onSuccess: () =>
      invalidar(qc, [
        ['household'],
        ['dashboard'],
        ['session'],
        ['reports'],
        ['reconciliation'],
      ]),
  })
}

export function useActualizarCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof accounts.update>[1]) =>
      accounts.update(id, input),
    onSuccess: () => invalidar(qc, [['accounts'], ['dashboard']]),
  })
}

/** Conciliación: se guarda el saldo real de la cuenta, no la diferencia. */
export function useConciliarCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, balanceCents }: { id: string; balanceCents: number }) =>
      accounts.setBalance(id, balanceCents),
    onSuccess: () => invalidar(qc, [['accounts'], ['dashboard']]),
  })
}

export function useArchivarCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => accounts.archive(id),
    onSuccess: () => invalidar(qc, [['accounts'], ['dashboard']]),
  })
}

/**
 * Borrado definitivo de una cuenta sin historia.
 *
 * Invalida lo mismo que archivar y además el ahorro: una cuenta de ahorro que
 * desaparece cambia el saldo que esa pantalla resume.
 */
export function useBorrarCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => accounts.remove(id),
    onSuccess: () => invalidar(qc, [['accounts'], ['dashboard'], ['savings']]),
  })
}

export function useCrearInvitacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: household.invitations.create,
    onSuccess: () => invalidar(qc, [['household'], ['session']]),
  })
}

// ── Bandeja de avisos ───────────────────────────────────────────────────────

/**
 * Lo que hay detrás de la campanita.
 *
 * La bandeja no es una tabla: se deriva del estado real en cada lectura, así
 * que cualquier cosa que mueva dinero la cambia. Por eso `staleTime` es corto y
 * `TRAS_MOVIMIENTO` no la invalida una por una — sale sola al volver a la
 * pantalla, que es cuando importa.
 */
export function useAvisos() {
  return useQuery({
    queryKey: keys.avisos,
    queryFn: () => notifications.bandeja(),
    // La campanita vive en el inicio: un minuto es suficiente para que un pago
    // registrado desde otra pantalla se refleje sin machacar la API.
    staleTime: 60 * 1000,
  })
}

/**
 * Marca la bandeja como vista y apaga el punto rojo.
 *
 * Invalida la propia bandeja porque el punto del inicio sale de `sinVer`: sin
 * esto, la campanita seguiría encendida hasta el siguiente refresco aunque la
 * pantalla ya se hubiera leído.
 */
export function useMarcarAvisosVistos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (hasta?: string) => notifications.marcarBandejaVista(hasta),
    // Solo la bandeja: las reglas y los recordatorios no cambian por leerla, y
    // colgar de la misma raíz no es motivo para pedirlos otra vez.
    onSuccess: () => invalidar(qc, [keys.avisos]),
  })
}

export function useReglasAviso() {
  return useQuery({ queryKey: keys.reglasAviso, queryFn: () => notifications.reglas() })
}

export function useActualizarReglaAviso() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      type,
      ...input
    }: { type: NotificationType } & Parameters<typeof notifications.actualizarRegla>[1]) =>
      notifications.actualizarRegla(type, input),
    onSuccess: () => invalidar(qc, [['notifications', 'rules']]),
  })
}

export function useRecordatorios() {
  return useQuery({ queryKey: keys.recordatorios, queryFn: () => notifications.recordatorios() })
}

export function useCrearRecordatorio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: notifications.crearRecordatorio,
    onSuccess: () => invalidar(qc, [['notifications', 'reminders']]),
  })
}

export function useActualizarRecordatorio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Partial<RecordatorioInput>) =>
      notifications.actualizarRecordatorio(id, input),
    onSuccess: () => invalidar(qc, [['notifications', 'reminders']]),
  })
}

export function useBorrarRecordatorio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => notifications.borrarRecordatorio(id),
    onSuccess: () => invalidar(qc, [['notifications', 'reminders']]),
  })
}

// ── Revisiones conjuntas (§11) ──────────────────────────────────────────────

/**
 * La próxima revisión, el historial y la cadencia.
 *
 * Una sola consulta para toda la pantalla porque el servidor las devuelve
 * juntas: partirlas en tres dejaría la cuenta regresiva dibujada antes de saber
 * si el hogar puede siquiera tener revisiones.
 */
export function useRevisiones() {
  return useQuery({ queryKey: keys.reviews, queryFn: () => reviews.overview() })
}

/**
 * Programar, completar y cambiar la cadencia solo tocan las revisiones.
 *
 * Nada de esto mueve un peso: completar una revisión **registra** lo que se
 * encontró, no lo corrige. Si el ahorro real no coincide, el ajuste se hace
 * después desde la meta o desde la cuenta, y esa sí es una escritura de dinero.
 * Invalidar el tablero aquí daría a entender lo contrario.
 */
export function useProgramarRevision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof reviews.schedule>[0]) => reviews.schedule(input),
    onSuccess: () => invalidar(qc, [['reviews']]),
  })
}

export function useCompletarRevision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: { id: string; confirmedTotalCents: number; notes?: string | null }) =>
      reviews.complete(id, input),
    onSuccess: () => invalidar(qc, [['reviews']]),
  })
}

/** Cada cuánto toca. Requiere rol de administración; el servidor responde 403 si no. */
export function useFrecuenciaRevisiones() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (frecuencia: ReviewFrequency | null) => reviews.setFrequency(frecuencia),
    // La respuesta ya es el panorama completo: se siembra para que el selector
    // no parpadee entre el valor viejo y el nuevo mientras vuelve la consulta.
    onSuccess: (data) => {
      qc.setQueryData(keys.reviews, data)
      invalidar(qc, [['reviews']])
    },
  })
}

// ── Comprobantes y evidencias (§23) ─────────────────────────────────────────

/**
 * Subir y borrar archivos.
 *
 * La invalidación es condicional porque los dos destinos no comparten consulta:
 * la evidencia de una revisión viaja dentro de `GET /reviews` y hay que
 * refrescarla, mientras que el comprobante de un gasto no aparece en ninguna
 * lista todavía —quien lo sube se queda con los metadatos que devuelve el POST—.
 * Invalidar siempre provocaría una recarga de revisiones cada vez que alguien
 * fotografía un ticket.
 */
export function useSubirAdjunto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      ...destino
    }: { file: File } & DestinoAdjunto & { declaredAmountCents?: number }) =>
      subirAdjunto(file, destino),
    onSuccess: (adjunto) => {
      if (adjunto.reviewId !== null) invalidar(qc, [['reviews']])
    },
  })
}

export function useBorrarAdjunto() {
  const qc = useQueryClient()
  return useMutation({
    // El destino viaja en la llamada porque la respuesta del borrado es un 204
    // vacío: después de borrarlo ya no hay forma de saber de dónde colgaba.
    mutationFn: ({ id }: { id: string; eraDeRevision?: boolean }) => borrarAdjunto(id),
    onSuccess: (_data, variables) => {
      if (variables.eraDeRevision) invalidar(qc, [['reviews']])
    },
  })
}

function invalidar(
  qc: ReturnType<typeof useQueryClient>,
  claves: ReadonlyArray<readonly string[]>,
): void {
  for (const clave of claves) {
    void qc.invalidateQueries({ queryKey: clave })
  }
}
